// src/services/pathologyTransactionService.js
import apiClient from "./apiClient";

const API_URL = `/pathology-transactions`;

const toPositiveId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return toPositiveId(
      value.id ??
        value.inventoryId ??
        value.invoiceId ??
        value.invoice_id ??
        value.labTestId ??
        value.lab_test_id
    );
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

const toPositiveQuantity = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return 0;
  return Math.abs(n);
};

/**
 * Ensure FKs are sent as both camelCase and snake_case so org
 * pathology_transactions inserts succeed for either DTO naming style.
 */
export const normalizePathologyTransactionRelationIds = (payload = {}) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }

  const next = { ...payload };

  const inventoryId = toPositiveId(
    next.inventoryId ?? next.inventory_id ?? next.inventory
  );
  if (inventoryId != null) {
    next.inventoryId = inventoryId;
    next.inventory_id = inventoryId;
  }

  const bomId = toPositiveId(next.bomId ?? next.bom_id ?? next.bom);
  if (bomId != null) {
    next.bomId = bomId;
    next.bom_id = bomId;
  }

  const invoiceId = toPositiveId(
    next.invoiceId ?? next.invoice_id ?? next.invoice
  );
  if (invoiceId != null) {
    next.invoiceId = invoiceId;
    next.invoice_id = invoiceId;
  }

  const prescriptionId = toPositiveId(
    next.prescriptionId ?? next.prescription_id ?? next.prescription
  );
  if (prescriptionId != null) {
    next.prescriptionId = prescriptionId;
    next.prescription_id = prescriptionId;
  }

  const patientId = toPositiveId(next.patientId ?? next.patient_id ?? next.patient);
  if (patientId != null) {
    next.patientId = patientId;
    next.patient_id = patientId;
  }

  const labTestId = toPositiveId(
    next.labTestId ?? next.lab_test_id ?? next.testId ?? next.test_id
  );
  if (labTestId != null) {
    next.labTestId = labTestId;
    next.lab_test_id = labTestId;
  }

  const admissionId = toPositiveId(
    next.admissionId ?? next.admission_id ?? next.admission
  );
  if (admissionId != null) {
    next.admissionId = admissionId;
    next.admission_id = admissionId;
  }

  const qty = toPositiveQuantity(
    next.deductedQuantity ?? next.deducted_quantity ?? next.quantity ?? next.addedQuantity
  );
  next.deductedQuantity = qty;
  next.deducted_quantity = qty;

  const itemName =
    next.itemName ||
    next.item_name ||
    next.pathItemName ||
    next.name ||
    "";
  if (itemName) {
    next.itemName = itemName;
    next.item_name = itemName;
  }

  if (next.transactionType) {
    next.transactionType = String(next.transactionType).toUpperCase();
    next.transaction_type = next.transactionType;
  }

  return next;
};

const isMissingPathologyTransactionApi = (error) => {
  const status = error?.response?.status;
  return status === 404 || status === 501 || status === 405;
};

export const createPathologyTransaction = async (transactionData) => {
  try {
    const payload = normalizePathologyTransactionRelationIds(transactionData);
    const response = await apiClient.post(API_URL, payload);
    return response.data;
  } catch (error) {
    if (!isMissingPathologyTransactionApi(error)) {
      console.error("Error creating pathology transaction:", error);
    }
    throw error;
  }
};

export const getAllPathologyTransactions = async () => {
  try {
    const response = await apiClient.get(API_URL);
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  } catch (error) {
    if (!isMissingPathologyTransactionApi(error)) {
      console.error("Error fetching pathology transactions:", error);
    }
    throw error;
  }
};

export const getPathologyTransactionsByPatientId = async (patientId) => {
  try {
    const response = await apiClient.get(`${API_URL}/patient/${patientId}`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    if (!isMissingPathologyTransactionApi(error)) {
      console.error("Error fetching pathology transactions by patient:", error);
    }
    throw error;
  }
};

/**
 * Record stock IN after inventory create. Never throws — add-stock must
 * succeed even when the pathology transaction table/API is not deployed yet.
 */
export const recordPathologyStockIn = async (transactionData = {}) => {
  try {
    return await createPathologyTransaction({
      ...transactionData,
      transactionType: transactionData.transactionType || "IN",
      transactionDate:
        transactionData.transactionDate || new Date().toISOString(),
    });
  } catch (error) {
    if (!isMissingPathologyTransactionApi(error)) {
      console.warn(
        "[pathologyTransactionService] Stock IN history not saved:",
        error?.response?.status || error?.message
      );
    }
    return null;
  }
};

/**
 * Record stock OUT after a lab-test result is added. Never throws — result
 * save / inventory deduct must still succeed if history insert is missing.
 * invoiceId here is the pathology TEST bill (PATHOLOGYTEST), not stock invoice.
 */
export const recordPathologyLabTestOut = async (context = {}) => {
  try {
    const labTestId = toPositiveId(
      context.labTestId ?? context.lab_test_id ?? context.testId
    );
    if (!labTestId) return [];

    let invoiceId = toPositiveId(context.invoiceId ?? context.invoice_id);
    if (!invoiceId) {
      const { getInvoiceList } = await import("./invoiceService.js");
      const {
        findPathologyTestInvoiceId,
      } = await import("../utils/pathTransactionHistoryUtils.js");
      const invoices = await getInvoiceList({ fullRange: true }).catch(() => []);
      invoiceId = toPositiveId(
        findPathologyTestInvoiceId(invoices, {
          invoiceId: context.invoiceId,
          prescriptionId: context.prescriptionId ?? context.prescription_id,
          patientId: context.patientId ?? context.patient_id,
        })
      );
    }

    let entries = Array.isArray(context.bomEntries) ? context.bomEntries : [];
    if (!entries.length) {
      const { getAllLabTests } = await import("./labtestService.js");
      const { getBOMsFromBaseTable } = await import("./labService.js");
      const { parseLabTestBomEntries } = await import("../utils/pathInventorySummary.js");
      const [labTests, boms] = await Promise.all([
        getAllLabTests().catch(() => []),
        getBOMsFromBaseTable().catch(() => []),
      ]);
      const labTest =
        context.labTest ||
        (Array.isArray(labTests)
          ? labTests.find((row) => Number(row?.id) === Number(labTestId))
          : null);
      const bomsById = new Map(
        (Array.isArray(boms) ? boms : []).map((bom) => [Number(bom.id), bom])
      );
      entries = parseLabTestBomEntries(labTest, bomsById);
    }
    if (!entries.length) {
      entries = [
        {
          bomId: context.bomId ?? null,
          itemName: context.labTestName || context.itemName || "",
          requiredQuantity: 1,
        },
      ];
    }

    const shared = {
      transactionType: "OUT",
      transactionDate: context.transactionDate || new Date().toISOString(),
      labTestId,
      labTestName:
        context.labTestName ||
        context.testName ||
        context.labTest?.name ||
        context.labTest?.testName ||
        "",
      patientId: context.patientId ?? context.patient_id ?? null,
      patientName: context.patientName ?? context.patient_name ?? "",
      patientType: context.patientType || context.patient_type || "",
      doctorName: context.doctorName || context.doctor_name || "",
      doctorId: context.doctorId ?? context.doctor_id ?? null,
      prescriptionId: context.prescriptionId ?? context.prescription_id ?? null,
      invoiceId,
      reportId: context.reportId ?? context.report_id ?? null,
      admissionId: context.admissionId ?? context.admission_id ?? null,
      userId: context.userId ?? context.user_id ?? null,
    };

    const saved = [];
    for (const entry of entries) {
      const row = await createPathologyTransaction({
        ...shared,
        bomId: entry.bomId ?? context.bomId ?? null,
        itemName: entry.itemName || shared.labTestName,
        deductedQuantity: entry.requiredQuantity ?? entry.quantity ?? 1,
      });
      saved.push(row);
    }
    return saved;
  } catch (error) {
    if (!isMissingPathologyTransactionApi(error)) {
      console.warn(
        "[pathologyTransactionService] Lab-test OUT history not saved:",
        error?.response?.status || error?.message
      );
    }
    return [];
  }
};

export const isPathologyTransactionApiUnavailable = isMissingPathologyTransactionApi;
